import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OrganizationService } from '../../services/organization.service';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { SpacesTreeComponent } from './spaces-tree.component';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SpacesTreeComponent],
  template: `
    <div class="p-8 max-w-7xl mx-auto">
      <div class="mb-6">
        <a routerLink="/organizations" class="text-gray-500 hover:text-white font-mono text-xs uppercase tracking-widest flex items-center gap-2 transition-colors inline-block">
          <i class="ph ph-arrow-left"></i> Volver a Instituciones
        </a>
      </div>

      <header class="flex justify-between items-end mb-8 border-l-4 border-zaxis-accent pl-4">
        <div>
          <h1 class="text-3xl font-mono font-black tracking-tight text-white uppercase">Miembros</h1>
          <p class="text-gray-400 mt-2 text-[10px] font-mono tracking-widest uppercase">ID: {{ orgId }}</p>
        </div>
        <button (click)="showInvitePanel = true" class="px-4 py-2 bg-zaxis-accent/10 border border-zaxis-accent text-zaxis-accent font-mono text-[10px] uppercase tracking-widest hover:bg-zaxis-accent hover:text-black transition-colors flex items-center gap-2">
          <i class="ph ph-user-plus text-lg"></i>
          Invitar Miembro
        </button>
      </header>

      <!-- Custom Brutalist Skeleton Loading State -->
      <div *ngIf="loading" class="bg-black border border-zaxis-border">
        <div class="border-b border-zaxis-border bg-zaxis-border/20 flex px-4 py-3">
          <div class="w-1/3 text-[10px] font-mono uppercase tracking-widest text-gray-400">Usuario</div>
          <div class="w-1/4 text-[10px] font-mono uppercase tracking-widest text-gray-400">UID</div>
          <div class="w-1/6 text-[10px] font-mono uppercase tracking-widest text-gray-400">Rol</div>
          <div class="w-1/4 text-[10px] font-mono uppercase tracking-widest text-gray-400">Fecha Ingreso</div>
        </div>
        <div *ngFor="let i of [1,2,3,4]" class="skeleton-box border-b border-zaxis-border/50 p-4 flex items-center gap-4 h-[60px]">
          <div class="w-1/3 flex items-center gap-3">
            <div class="skeleton-text w-8 h-8 rounded-full" style="margin: 0; min-height: 2rem;"></div>
            <div class="skeleton-text w-32" style="margin: 0"></div>
          </div>
          <div class="w-1/4">
            <div class="skeleton-text w-24" style="margin: 0"></div>
          </div>
          <div class="w-1/6">
            <div class="skeleton-text w-16 h-6" style="margin: 0; min-height: 1.5rem;"></div>
          </div>
          <div class="w-1/4">
            <div class="skeleton-text w-24" style="margin: 0"></div>
          </div>
        </div>
      </div>

      <div *ngIf="!loading && users.length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-users text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">No hay miembros registrados.</p>
      </div>

      <div *ngIf="!loading && users.length > 0" class="bg-black border border-zaxis-border">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="border-b border-zaxis-border bg-zaxis-border/20">
              <th class="p-4 text-[10px] font-mono uppercase tracking-widest text-gray-400">Usuario</th>
              <th class="p-4 text-[10px] font-mono uppercase tracking-widest text-gray-400 hidden md:table-cell">UID</th>
              <th class="p-4 text-[10px] font-mono uppercase tracking-widest text-gray-400">Rol</th>
              <th class="p-4 text-[10px] font-mono uppercase tracking-widest text-gray-400 hidden md:table-cell">Fecha Ingreso</th>
              <th class="p-4 text-[10px] font-mono uppercase tracking-widest text-gray-400 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let user of users" class="border-b border-zaxis-border/50 hover:bg-white/5 transition-colors">
              <td class="p-4">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full bg-zaxis-border/50 flex items-center justify-center">
                    <i class="ph ph-user text-gray-300"></i>
                  </div>
                  <span class="font-mono text-sm text-white">{{ user.email }}</span>
                </div>
              </td>
              <td class="p-4 text-xs font-mono text-gray-500 hidden md:table-cell">{{ user.uid }}</td>
              <td class="p-4">
                <span class="px-2 py-1 text-[9px] font-mono uppercase tracking-widest border"
                      [ngClass]="{
                        'border-zaxis-accent text-zaxis-accent bg-zaxis-accent/10': user.role === 'ORG_ADMIN',
                        'border-purple-400 text-purple-400 bg-purple-400/10': user.role === 'ORG_EDITOR',
                        'border-gray-400 text-gray-400 bg-gray-400/10': user.role === 'ORG_VIEWER'
                      }">
                  {{ user.role }}
                </span>
              </td>
              <td class="p-4 text-xs font-mono text-gray-500 hidden md:table-cell">{{ user.addedAt | date:'medium' }}</td>
              <td class="p-4 text-right">
                <div class="flex items-center justify-end gap-2">
                  <button (click)="openEditRole(user)" class="w-8 h-8 flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-white transition-colors border border-transparent hover:border-zaxis-border" title="Cambiar Rol">
                    <i class="ph ph-pencil-simple text-lg"></i>
                  </button>
                  <button (click)="confirmRemoveUser(user)" class="w-8 h-8 flex items-center justify-center hover:bg-zaxis-danger/10 text-gray-400 hover:text-zaxis-danger transition-colors border border-transparent hover:border-zaxis-danger/30" title="Eliminar Miembro">
                    <i class="ph ph-trash text-lg"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Spaces Tree Component -->
      @defer {
        <app-spaces-tree [orgId]="orgId"></app-spaces-tree>
      }

      <!-- Invite/Edit Slide-over Panel -->
      <div *ngIf="showInvitePanel || showEditPanel" class="fixed inset-0 z-50 flex justify-end">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" (click)="closePanel()"></div>
        <div class="relative w-full max-w-md bg-zaxis-card border-l border-zaxis-border shadow-none flex flex-col animate-panel-in">
          <div class="flex items-center justify-between p-6 border-b border-zaxis-border">
            <h2 class="text-[11px] font-mono uppercase tracking-widest text-gray-400">{{ showInvitePanel ? 'Invitar Miembro' : 'Editar Miembro' }}</h2>
            <button (click)="closePanel()" class="text-gray-500 hover:text-white transition-colors text-xl font-mono">&times;</button>
          </div>

          <div class="flex-1 p-6 space-y-6">
            <div *ngIf="showInvitePanel">
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-2">Email del usuario</label>
              <input
                [(ngModel)]="newEmail"
                type="email"
                class="w-full bg-zaxis-dark border border-zaxis-border px-4 py-3 text-white focus:outline-none focus:border-zaxis-accent transition-all font-mono tracking-widest rounded-none"
                placeholder="usuario@ejemplo.com">
            </div>

            <div *ngIf="showEditPanel">
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-2">Usuario a editar</label>
              <div class="px-4 py-3 bg-zaxis-dark border border-zaxis-border text-white font-mono text-sm">
                {{ editingUser?.email }}
              </div>
            </div>

            <div>
              <label class="block text-[11px] text-gray-500 font-mono uppercase tracking-widest mb-3">Rol de la Institución</label>
              <div class="space-y-3">
                <button (click)="newRole = 'ORG_ADMIN'" 
                        [class]="newRole === 'ORG_ADMIN' ? 'border-zaxis-accent bg-zaxis-accent/10 text-zaxis-accent shadow-pixel' : 'border-zaxis-border text-gray-400 hover:border-gray-500 hover:text-gray-300'" 
                        class="w-full border p-4 flex flex-col text-left transition-all rounded-none cursor-pointer group">
                  <div class="flex items-center gap-2 font-mono uppercase tracking-widest text-xs mb-1 font-bold">
                    <i class="ph text-lg" [ngClass]="newRole === 'ORG_ADMIN' ? 'ph-check-square-offset' : 'ph-square'"></i>
                    Administrador
                  </div>
                  <div class="text-[10px] font-mono text-gray-500 tracking-wide ml-6 group-hover:text-gray-400 transition-colors">Acceso total para gestionar usuarios y datos.</div>
                </button>

                <button (click)="newRole = 'ORG_EDITOR'" 
                        [class]="newRole === 'ORG_EDITOR' ? 'border-purple-400 bg-purple-400/10 text-purple-400 shadow-pixel' : 'border-zaxis-border text-gray-400 hover:border-gray-500 hover:text-gray-300'" 
                        class="w-full border p-4 flex flex-col text-left transition-all rounded-none cursor-pointer group">
                  <div class="flex items-center gap-2 font-mono uppercase tracking-widest text-xs mb-1 font-bold">
                    <i class="ph text-lg" [ngClass]="newRole === 'ORG_EDITOR' ? 'ph-check-square-offset' : 'ph-square'"></i>
                    Editor
                  </div>
                  <div class="text-[10px] font-mono text-gray-500 tracking-wide ml-6 group-hover:text-gray-400 transition-colors">Puede crear y modificar contenido técnico.</div>
                </button>

                <button (click)="newRole = 'ORG_VIEWER'" 
                        [class]="newRole === 'ORG_VIEWER' ? 'border-gray-300 bg-gray-400/10 text-white shadow-pixel' : 'border-zaxis-border text-gray-400 hover:border-gray-500 hover:text-gray-300'" 
                        class="w-full border p-4 flex flex-col text-left transition-all rounded-none cursor-pointer group">
                  <div class="flex items-center gap-2 font-mono uppercase tracking-widest text-xs mb-1 font-bold">
                    <i class="ph text-lg" [ngClass]="newRole === 'ORG_VIEWER' ? 'ph-check-square-offset' : 'ph-square'"></i>
                    Lector
                  </div>
                  <div class="text-[10px] font-mono text-gray-500 tracking-wide ml-6 group-hover:text-gray-400 transition-colors">Acceso de solo lectura. No puede realizar cambios.</div>
                </button>
              </div>
            </div>

            <div *ngIf="inviteError" class="text-zaxis-danger text-[11px] font-mono tracking-wide bg-zaxis-danger/10 p-3 border border-zaxis-danger/20">
              {{ inviteError }}
            </div>

            <!-- Link de Invitación (Éxito) -->
            <div *ngIf="generatedInviteLink" class="mt-6 border border-zaxis-accent bg-zaxis-accent/5 p-4 flex flex-col gap-3">
              <div class="flex items-center gap-2 text-zaxis-accent font-bold text-xs font-mono uppercase tracking-widest">
                <i class="ph ph-check-circle text-lg"></i>
                <span>Usuario Creado con Éxito</span>
              </div>
              <p class="text-gray-400 text-[10px] font-mono leading-relaxed">
                Copia y envía este enlace seguro al usuario para que configure su contraseña y active su cuenta.
              </p>
              
              <div class="flex items-center mt-2 border border-zaxis-border">
                <input type="text" [value]="generatedInviteLink" readonly class="w-full bg-black/50 text-gray-300 font-mono text-[10px] p-2 outline-none">
                <button (click)="copyInviteLink()" 
                        [class]="linkCopied ? 'bg-zaxis-accent text-black hover:bg-cyan-400' : 'bg-zaxis-border hover:bg-gray-700 text-white'"
                        class="px-4 py-2 font-mono text-[10px] font-bold uppercase transition-colors shrink-0 min-w-[80px]">
                  {{ linkCopied ? '¡COPIADO!' : 'COPIAR' }}
                </button>
              </div>
            </div>

            <button *ngIf="!generatedInviteLink && !showEditPanel"
              (click)="inviteUser()"
              [disabled]="isInviting || !newEmail.trim()"
              class="px-6 py-3 bg-white hover:bg-gray-200 text-black font-mono font-bold uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full rounded-none mt-4">
              {{ isInviting ? 'INVITANDO...' : 'ENVIAR INVITACIÓN' }}
            </button>

            <button *ngIf="showEditPanel"
              (click)="updateUserRole()"
              [disabled]="isInviting"
              class="px-6 py-3 bg-zaxis-accent hover:bg-cyan-400 text-black font-mono font-bold uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full rounded-none mt-4">
              {{ isInviting ? 'GUARDANDO...' : 'GUARDAR CAMBIOS' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div *ngIf="userToDelete" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" (click)="cancelRemove()"></div>
        <div class="relative bg-black border border-zaxis-danger w-full max-w-md shadow-none flex flex-col">
          <div class="flex items-center gap-3 p-6 border-b border-zaxis-danger/30 bg-zaxis-danger/10">
            <i class="ph ph-warning-circle text-2xl text-zaxis-danger"></i>
            <h2 class="text-sm font-mono uppercase tracking-widest text-zaxis-danger font-bold">Eliminar Miembro</h2>
          </div>
          
          <div class="p-6">
            <p class="text-gray-300 font-mono text-[11px] mb-4">
              ¿Estás seguro de que deseas revocar el acceso de <span class="text-white font-bold">{{ userToDelete.email }}</span>?
            </p>
            <p class="text-gray-500 font-mono text-[10px] leading-relaxed">
              El usuario perderá sus permisos en el Dashboard inmediatamente. El registro será suspendido y retenido en bóveda según la política de la institución.
            </p>
            
            <div *ngIf="inviteError" class="mt-4 text-zaxis-danger text-[11px] font-mono tracking-wide bg-zaxis-danger/10 p-3 border border-zaxis-danger/20">
              {{ inviteError }}
            </div>
          </div>
          
          <div class="p-4 border-t border-zaxis-border flex justify-end gap-3 bg-zaxis-dark">
            <button (click)="cancelRemove()" [disabled]="isInviting" class="px-6 py-2 border border-zaxis-border text-gray-400 font-mono text-[10px] uppercase tracking-widest hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button (click)="executeRemoveUser()" [disabled]="isInviting" class="px-6 py-2 bg-zaxis-danger text-black font-mono text-[10px] font-bold uppercase tracking-widest hover:bg-red-500 transition-colors disabled:opacity-50">
              {{ isInviting ? 'EJECUTANDO...' : 'ELIMINAR' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  `,
  styles: [`
    @keyframes slideIn {
      from { transform: translateX(100%); }
      to { transform: translateX(0); }
    }
    .animate-panel-in {
      animation: slideIn 0.3s ease-out forwards;
    }
  `]
})
export class OrganizationDetailComponent implements OnInit {
  private orgService = inject(OrganizationService);
  private route = inject(ActivatedRoute);

  orgId: string = '';
  users: any[] = [];
  loading = true;

  showInvitePanel = false;
  showEditPanel = false;
  editingUser: any = null;
  userToDelete: any = null;
  generatedInviteLink: string | null = null;
  linkCopied = false;

  isInviting = false;
  newEmail = '';
  newRole = 'ORG_ADMIN'; // Default
  inviteError = '';

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.orgId = params.get('id') || '';
      if (this.orgId) {
        this.loadUsers();
      }
    });
  }

  loadUsers() {
    this.loading = true;
    this.orgService.getOrganizationUsers(this.orgId).subscribe({
      next: (res) => {
        this.users = res || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading members', err);
        this.loading = false;
      }
    });
  }

  closePanel() {
    this.showInvitePanel = false;
    this.showEditPanel = false;
    this.editingUser = null;
    this.inviteError = '';
    this.generatedInviteLink = null;
  }

  openEditRole(user: any) {
    this.editingUser = user;
    this.newRole = user.role;
    this.showEditPanel = true;
    this.inviteError = '';
  }

  updateUserRole() {
    if (!this.editingUser) return;
    this.isInviting = true;
    this.inviteError = '';

    this.orgService.updateOrganizationUserRole(this.orgId, this.editingUser.uid, this.newRole).subscribe({
      next: (res) => {
        this.editingUser.role = this.newRole;
        this.isInviting = false;
        this.closePanel();
      },
      error: (err) => {
        this.inviteError = 'No se pudo actualizar el rol.';
        this.isInviting = false;
      }
    });
  }

  confirmRemoveUser(user: any) {
    this.userToDelete = user;
    this.inviteError = '';
  }

  cancelRemove() {
    this.userToDelete = null;
    this.inviteError = '';
  }

  executeRemoveUser() {
    if (!this.userToDelete) return;
    this.isInviting = true;
    this.inviteError = '';

    this.orgService.removeOrganizationUser(this.orgId, this.userToDelete.uid).subscribe({
      next: () => {
        this.users = this.users.filter(u => u.uid !== this.userToDelete.uid);
        this.isInviting = false;
        this.userToDelete = null;
      },
      error: (err) => {
        this.inviteError = 'Error al revocar el acceso del usuario.';
        this.isInviting = false;
      }
    });
  }

  inviteUser() {
    if (!this.newEmail.trim()) return;

    this.isInviting = true;
    this.inviteError = '';
    this.generatedInviteLink = null;

    this.orgService.addOrganizationUser(this.orgId, this.newEmail.trim(), this.newRole).subscribe({
      next: (res) => {
        if (res) {
          this.users.unshift(res);

          if (res.inviteLink) {
            this.generatedInviteLink = res.inviteLink;
            this.isInviting = false;
            // No cerramos el panel para que pueda copiar el link
          } else {
            this.isInviting = false;
            this.closePanel();
            this.newEmail = '';
          }
        }
      },
      error: (err) => {
        this.inviteError = 'No se pudo añadir el miembro. Verifica el formato del correo.';
        this.isInviting = false;
      }
    });
  }

  copyInviteLink() {
    if (this.generatedInviteLink) {
      navigator.clipboard.writeText(this.generatedInviteLink).then(() => {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      });
    }
  }
}
